import { FilesApi } from '@cibo/core/src/api/FilesAPI'
import type { FileShape } from '@cibo/core/src/types/FileTypes'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueries } from '@tanstack/react-query'
import { connectField } from 'dynamic-uniforms'
import { useSnackbar } from 'notistack'
import { ChangeEvent, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateFile } from '../../../../queries/useFiles'
import { useCiboAutoForm } from '../CiboAutoFormContext'
import { FieldLabel } from '../FieldLabel'
import { ValidationMessages } from '../ValidationMessages'
import { AddExistingFileModal } from './AddExistingFileModal'
import { ACCEPTED_EXTENSIONS, DEFAULT_MAX_FILES } from './constants'
import { DashedUploadBox } from './DashedUploadBox'
import { FileUploadFieldProps } from './types'
import { parseMaxSize } from './utils'

function FileUploadInput({
  name,
  value,
  onChange,
  label,
  disabled,
  error,
  required,
  'ui-params': uiParams,
}: FileUploadFieldProps) {
  const { t } = useTranslation('@cibo/ui/FileUploadField')
  const { enqueueSnackbar } = useSnackbar()
  const { touch } = useCiboAutoForm()
  const createFile = useCreateFile()
  const inputRef = useRef<HTMLInputElement>(null)

  const fileIds = value ?? []
  const maxFiles = uiParams?.maxFiles ?? DEFAULT_MAX_FILES
  const extensions = uiParams?.extensions ?? ACCEPTED_EXTENSIONS
  const fileType = uiParams?.fileType
  const maxSizeBytes = parseMaxSize(uiParams?.maxSize)
  const allowNoFiles = uiParams?.allowNoFiles ?? false
  const noFilesMessage = uiParams?.noFilesMessage ?? t('willNotUploadFiles')
  const remainingCount = maxFiles - fileIds.length

  const fileQueries = useQueries({
    queries: fileIds.map(fileId => ({
      queryKey: ['file', fileId],
      queryFn: () => FilesApi.getFile(fileId).then(response => response.data.items[0]),
    })),
  })

  const loadedFiles = fileQueries
    .map(q => q.data)
    .filter((f): f is FileShape => typeof f !== 'undefined')

  const isUploading = createFile.isPending
  const [addExistingOpen, setAddExistingOpen] = useState(false)

  const handleAddExisting = useCallback(
    (file: FileShape) => {
      const newIds = [...fileIds, file.id]
      onChange(newIds)
      touch(name)
    },
    [fileIds, onChange, touch, name]
  )

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const candidates = Array.from(e.target.files ?? []).slice(0, remainingCount)
    if (!candidates.length) return

    // Validate extension
    const rejected = candidates.filter(file => {
      const ext =
        file.name
          .split('.')
          .pop()
          ?.toLowerCase() ?? ''
      return !extensions.includes(ext)
    })
    if (rejected.length > 0) {
      enqueueSnackbar(t('filesRejectedExtension', { extensions: extensions.join(', ') }), {
        variant: 'error',
      })
      e.target.value = ''
      return
    }

    // Validate size
    if (maxSizeBytes !== undefined) {
      const oversized = candidates.filter(file => file.size > maxSizeBytes)
      if (oversized.length > 0) {
        enqueueSnackbar(t('filesRejectedSize'), { variant: 'error' })
        e.target.value = ''
        return
      }
    }

    try {
      const created = await Promise.all(
        candidates.map(file => createFile.mutateAsync({ file, fileType }))
      )
      const newIds = [...fileIds, ...created.map(f => f?.id).filter((id): id is string => !!id)]
      onChange(newIds)
      touch(name)
      enqueueSnackbar(t('filesUploaded', { count: candidates.length }), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('fileUploadError'), { variant: 'error' })
    }

    // Reset so the same file can be selected again
    e.target.value = ''
  }

  const handleRemove = (fileId: string) => {
    const newIds = fileIds.filter(id => id !== fileId)
    onChange(newIds)
    touch(name)
  }

  const acceptAttr = extensions.map(ext => `.${ext}`).join(',')

  return (
    <FormControl fullWidth disabled={disabled}>
      {label && <FieldLabel label={label} required={required} />}

      <Stack spacing={1} data-testid={`file-upload-field-${name}`}>
        {loadedFiles.length > 0 && (
          <Table size="small">
            <TableBody>
              {loadedFiles.map(file => (
                <TableRow key={file.id} data-testid={`file-row-${file.id}`}>
                  <TableCell>
                    <Typography variant="body2">{file.name}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('removeFile')}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={() => handleRemove(file.id)}
                          data-testid={`file-remove-${file.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {remainingCount > 0 && !disabled && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={acceptAttr}
              multiple={maxFiles > 1}
              style={{ display: 'none' }}
              onChange={handleInputChange}
              data-testid={`file-upload-input-${name}`}
            />
            <Box>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setAddExistingOpen(true)}
                data-testid={`file-upload-add-existing-${name}`}
              >
                {t('addExistingFile')}
              </Button>
            </Box>
            <DashedUploadBox
              onClick={() => !isUploading && inputRef.current?.click()}
              role="button"
              aria-label={t('clickToUpload')}
              data-testid={`file-upload-dropzone-${name}`}
            >
              <Stack alignItems="center" spacing={1}>
                {isUploading ? (
                  <CircularProgress size={44} />
                ) : (
                  <CloudUploadIcon style={{ fontSize: 44 }} />
                )}
                <Typography>
                  {isUploading ? t('uploading') : t('dropOrClick', { count: remainingCount })}
                </Typography>
              </Stack>
            </DashedUploadBox>
          </>
        )}
      </Stack>

      <AddExistingFileModal
        open={addExistingOpen}
        onClose={() => setAddExistingOpen(false)}
        onAdd={handleAddExisting}
        extensions={extensions}
        maxSizeBytes={maxSizeBytes}
        alreadySelectedIds={fileIds}
      />

      {allowNoFiles && (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={value !== undefined && value.length === 0}
                onChange={(_, checked) => {
                  onChange(checked ? [] : undefined)
                  touch(name)
                }}
                disabled={disabled || fileIds.length > 0}
                data-testid={`file-upload-no-files-checkbox-${name}`}
              />
            }
            label={noFilesMessage}
          />
        </FormGroup>
      )}

      <ValidationMessages fieldName={name} error={error} />
    </FormControl>
  )
}

export default connectField(FileUploadInput)
